import React, { useState } from 'react';
import { Gift, Check, Mail, Download, ShoppingCart } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { useToast } from '../hooks/use-toast';

const BuonoRegalo = () => {
  const { toast } = useToast();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('email');
  const [isProcessing, setIsProcessing] = useState(false);

  const predefinedAmounts = [25, 50, 75, 100, 150, 200];

  const getSelectedAmountValue = () => {
    return selectedAmount === 'custom' ? parseFloat(customAmount) || 0 : selectedAmount;
  };

  const handlePurchase = async () => {
    const amount = getSelectedAmountValue();
    
    if (!amount || amount < 10) {
      toast({
        title: "Importo non valido",
        description: "L'importo minimo per un buono regalo è di €10.",
        variant: "destructive"
      });
      return;
    }

    if (deliveryMethod === 'email' && !recipientEmail) {
      toast({
        title: "Email mancante",
        description: "Inserisci l'email del destinatario per l'invio.",
        variant: "destructive"
      });
      return;
    }

    if (!recipientName || !senderName) {
      toast({
        title: "Dati mancanti",
        description: "Inserisci il nome del destinatario e del mittente.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);
    
    // Mock purchase process
    setTimeout(() => {
      toast({
        title: "Buono regalo acquistato!",
        description: `Buono regalo di €${amount.toFixed(2)} ${deliveryMethod === 'email' ? 'inviato' : 'generato'} con successo.`,
      });
      
      // Reset form
      setSelectedAmount(null);
      setCustomAmount('');
      setRecipientEmail('');
      setRecipientName('');
      setSenderName('');
      setMessage('');
      setIsProcessing(false);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-6">
            <Gift className="w-10 h-10" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Buono Regalo
          </h1>
          <p className="text-xl max-w-3xl mx-auto">
            Regala un'esperienza unica: la possibilità di creare un liquore personalizzato.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column - Gift Card Configuration */}
            <div className="space-y-8">
              {/* Amount Selection */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Scegli l'Importo</h2>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                  {predefinedAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => {setSelectedAmount(amount); setCustomAmount('');}}
                      className={`p-4 border-2 rounded-lg font-semibold transition-colors duration-200 ${
                        selectedAmount === amount
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-200 hover:border-purple-300 text-gray-700'
                      }`}
                    >
                      €{amount}
                    </button>
                  ))}
                </div>
                
                <div className="space-y-4">
                  <button
                    onClick={() => setSelectedAmount('custom')}
                    className={`w-full p-4 border-2 rounded-lg font-semibold transition-colors duration-200 ${
                      selectedAmount === 'custom'
                        ? 'border-purple-600 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-purple-300 text-gray-700'
                    }`}
                  >
                    Importo Personalizzato
                  </button>
                  
                  {selectedAmount === 'custom' && (
                    <div>
                      <input
                        type="number"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder="Inserisci importo (min. €10)"
                        min="10"
                        step="0.01"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Delivery Method */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Modalità di Consegna</h2>
                
                <div className="space-y-4">
                  <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors duration-200 hover:bg-gray-50">
                    <input
                      type="radio"
                      name="delivery"
                      value="email"
                      checked={deliveryMethod === 'email'}
                      onChange={(e) => setDeliveryMethod(e.target.value)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 border-2 rounded-full mr-3 ${
                      deliveryMethod === 'email' ? 'border-purple-600 bg-purple-600' : 'border-gray-300'
                    }`}>
                      {deliveryMethod === 'email' && <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center">
                        <Mail className="w-5 h-5 text-purple-600 mr-2" />
                        <span className="font-semibold text-gray-900">Invio via Email</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Il buono sarà inviato direttamente al destinatario</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors duration-200 hover:bg-gray-50">
                    <input
                      type="radio"
                      name="delivery"
                      value="download"
                      checked={deliveryMethod === 'download'}
                      onChange={(e) => setDeliveryMethod(e.target.value)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 border-2 rounded-full mr-3 ${
                      deliveryMethod === 'download' ? 'border-purple-600 bg-purple-600' : 'border-gray-300'
                    }`}>
                      {deliveryMethod === 'download' && <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center">
                        <Download className="w-5 h-5 text-purple-600 mr-2" />
                        <span className="font-semibold text-gray-900">Download Immediato</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Scarica il buono per stamparlo o inviarlo tu stesso</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Recipient Information */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Informazioni Regalo</h2>
                
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome Destinatario *
                      </label>
                      <input
                        type="text"
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Nome del destinatario"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Il Tuo Nome *
                      </label>
                      <input
                        type="text"
                        value={senderName}
                        onChange={(e) => setSenderName(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Il tuo nome"
                      />
                    </div>
                  </div>
                  
                  {deliveryMethod === 'email' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Destinatario *
                      </label>
                      <input
                        type="email"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="email@esempio.com"
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Messaggio Personalizzato (Opzionale)
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-vertical"
                      placeholder="Aggiungi un messaggio speciale per il destinatario..."
                      maxLength={500}
                    />
                    <p className="text-xs text-gray-500 mt-1">{message.length}/500 caratteri</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Preview and Purchase */}
            <div className="space-y-8">
              {/* Gift Card Preview */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Anteprima Buono Regalo</h2>
                
                <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg p-8 text-white text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                    <Gift className="w-8 h-8" />
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-2">BUONO REGALO</h3>
                  <p className="text-lg mb-4">Liquore Personalizzato</p>
                  
                  {getSelectedAmountValue() > 0 && (
                    <div className="text-4xl font-bold mb-4">
                      €{getSelectedAmountValue().toFixed(2)}
                    </div>
                  )}
                  
                  <div className="border-t border-white/30 pt-4 mt-4 text-sm">
                    {recipientName && <p className="mb-1">Per: <strong>{recipientName}</strong></p>}
                    {senderName && <p className="mb-1">Da: <strong>{senderName}</strong></p>}
                    {message && (
                      <div className="mt-3 p-3 bg-white/10 rounded text-xs italic">
                        "{message}"
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Purchase Summary */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Riepilogo</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Valore del buono</span>
                    <span className="font-medium">
                      {getSelectedAmountValue() > 0 ? `€${getSelectedAmountValue().toFixed(2)}` : 'Non selezionato'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-green-600">
                    <span>Spedizione</span>
                    <span className="font-medium">Gratuita</span>
                  </div>
                  
                  <div className="border-t pt-4">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Totale</span>
                      <span className="text-purple-600">
                        {getSelectedAmountValue() > 0 ? `€${getSelectedAmountValue().toFixed(2)}` : '€0.00'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={handlePurchase}
                  disabled={isProcessing || getSelectedAmountValue() < 10}
                  className={`w-full flex items-center justify-center px-6 py-4 rounded-lg font-semibold transition-colors duration-200 ${
                    isProcessing || getSelectedAmountValue() < 10
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Elaborazione...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5 mr-3" />
                      Acquista Buono Regalo
                    </>
                  )}
                </button>
              </div>

              {/* Benefits */}
              <div className="bg-purple-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Vantaggi del Buono Regalo</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                    Validità di 12 mesi dalla data di emissione
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                    Utilizzabile per tutti i prodotti del nostro catalogo
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                    Combinabile con altre promozioni
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                    Spedizione sempre gratuita
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default BuonoRegalo;